import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Send, Bot, User, ExternalLink, Plus, MessageSquare, Trash2, Menu, X, Sparkles, Search, Heart, Bookmark } from 'lucide-react';
import { Link } from 'react-router-dom';
import { apiClient } from '../../api/apiClient';
import CategoryManager from '../../components/categories/CategoryManager';
import { ThinkingOrb } from 'thinking-orbs';

interface Message {
  role: 'user' | 'model';
  text: string;
  products?: any[];
}

export const PersonalShopperAdmin: React.FC = () => {
  const defaultMessages: Message[] = [
    {
      role: 'model',
      text: 'Bonjour ! Je suis l\'IA Personal Shopper de Pépite. Que recherchez-vous aujourd\'hui ? Plus vous me donnez de détails, mieux je pourrai vous trouver le vêtement idéal !',
    }
  ];

  const [sessions, setSessions] = useState<any[]>([]);
  const [currentSessionId, setCurrentSessionId] = useState<string | null>(null);
  const [messages, setMessages] = useState<Message[]>(defaultMessages);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false); // Fermé par défaut sur mobile
  const [searchQuery, setSearchQuery] = useState('');
  const [thinkingMessageIndex, setThinkingMessageIndex] = useState(0);
  const [showRateLimitModal, setShowRateLimitModal] = useState(false);
  const [collectionModalProductId, setCollectionModalProductId] = useState<string | null>(null);
  const [likedProducts, setLikedProducts] = useState<Set<string>>(new Set());
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const thinkingMessages = [
    "Analyse de votre demande...",
    "Recherche dans les collections...",
    "Sélection des pépites...",
    "Génération des recommandations..."
  ];

  const thinkingOrbStates: Array<'working' | 'searching' | 'solving' | 'listening' | 'composing' | 'shaping'> = [
    'listening',
    'searching',
    'solving',
    'composing'
  ];

  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (isLoading) {
      setThinkingMessageIndex(0);
      interval = setInterval(() => {
        setThinkingMessageIndex(prev => (prev + 1) % thinkingMessages.length);
      }, 2500);
    }
    return () => clearInterval(interval);
  }, [isLoading]);

  const filteredSessions = sessions.filter(session => {
    const query = searchQuery.toLowerCase();
    const titleMatch = (session.title || 'Discussion').toLowerCase().includes(query);
    const messagesMatch = session.messages?.some((msg: Message) => 
      msg.text && msg.text.toLowerCase().includes(query)
    );
    return titleMatch || messagesMatch;
  });

  const fetchSessions = async () => {
    try {
      const data = await apiClient.shopper.getSessions();
      if (data.success) {
        setSessions(data.data);
      }
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    fetchSessions();
  }, []);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, isLoading]);

  const handleNewSession = () => {
    setCurrentSessionId(null);
    setMessages(defaultMessages);
    setIsSidebarOpen(false); // Ferme la sidebar sur mobile après sélection
  };

  const handleLoadSession = async (id: string) => {
    try {
      const data = await apiClient.shopper.getSession(id);
      if (data.success) {
        setCurrentSessionId(data.data.id);
        setMessages(data.data.messages && data.data.messages.length > 0 ? data.data.messages : defaultMessages);
        setIsSidebarOpen(false); // Ferme la sidebar sur mobile
      }
    } catch (error) {
      console.error(error);
    }
  };

  const handleDeleteSession = async (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    if (!window.confirm("Supprimer cette conversation ?")) return;
    try {
      await apiClient.shopper.deleteSession(id);
      fetchSessions();
      if (currentSessionId === id) {
        handleNewSession();
      }
    } catch (error) {
      console.error(error);
    }
  };

  const handleSubmit = async (e?: React.FormEvent, overrideInput?: string) => {
    e?.preventDefault();
    const messageToUse = overrideInput || input;
    if (!messageToUse.trim() || isLoading) return;

    const userMessage = messageToUse.trim();
    setInput('');
    const currentMessages = [...messages, { role: 'user' as const, text: userMessage }];
    setMessages(currentMessages);
    setIsLoading(true);

    try {
      const data = await apiClient.shopper.ask(userMessage, messages.slice(1));
      let updatedMessages = currentMessages;
      
      if (data.success) {
        updatedMessages = [...currentMessages, { 
          role: 'model' as const, 
          text: data.data.text,
          products: data.data.products 
        }];
        setMessages(updatedMessages);
      } else {
        throw new Error(data.error || 'Erreur inconnue');
      }

      // Save to backend
      const saveData = await apiClient.shopper.saveSession(currentSessionId, {
        title: userMessage.substring(0, 30) + (userMessage.length > 30 ? '...' : ''),
        messages: updatedMessages
      });
      if (saveData.success && !currentSessionId) {
        setCurrentSessionId(saveData.data.id);
        fetchSessions();
      }

    } catch (error: any) {
      if (error.response && error.response.status === 429) {
        setIsLoading(false);
        setShowRateLimitModal(true);
        // Remove the temporary user message
        setMessages(currentMessages.slice(0, -1));
        return;
      }
      
      console.error(error);
      setMessages(prev => [...prev, { 
        role: 'model', 
        text: 'Désolé, une erreur s\'est produite de mon côté. Je n\'ai pas pu traiter votre demande.' 
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="relative flex flex-col h-[calc(100vh-64px)] md:h-full bg-gray-50 dark:bg-gray-950 overflow-hidden font-sans">
      
      {/* Background decoration */}
      <div className="absolute inset-0 pointer-events-none opacity-50 dark:opacity-20 flex justify-center">
        <div className="w-full max-w-4xl h-full bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-pepite-100/50 via-transparent to-transparent blur-3xl"></div>
      </div>

      {/* --- HEADER --- */}
      <header className="relative z-20 shrink-0 bg-white/80 dark:bg-gray-900/80 backdrop-blur-md border-b border-gray-200/50 dark:border-gray-800/50 px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button 
            onClick={() => setIsSidebarOpen(!isSidebarOpen)}
            className="p-2 -ml-2 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full transition-colors md:hidden"
            aria-label="Menu Historique"
          >
            <Menu size={22} />
          </button>
          
          <div className="flex items-center gap-3">
            <div className="relative">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-tr from-pepite-600 to-pepite-400 flex items-center justify-center text-white shadow-lg shadow-pepite-500/20">
                <Bot size={22} />
              </div>
              <div className="absolute -bottom-1 -right-1 w-4 h-4 bg-green-500 border-2 border-white dark:border-gray-900 rounded-full"></div>
            </div>
            <div>
              <h2 className="font-bold text-gray-900 dark:text-white leading-tight">IA Shopper</h2>
              <p className="text-[11px] font-medium text-pepite-600 dark:text-pepite-400 flex items-center gap-1">
                <Sparkles size={10} /> Assistant intelligent
              </p>
            </div>
          </div>
        </div>

        {/* Bouton Desktop pour ouvrir historique */}
        <button 
          onClick={() => setIsSidebarOpen(!isSidebarOpen)}
          className="hidden md:flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-gray-600 hover:text-pepite-600 dark:text-gray-300 dark:hover:text-pepite-400 bg-gray-100/50 hover:bg-pepite-50 dark:bg-gray-800/50 dark:hover:bg-pepite-900/30 rounded-lg transition-all"
        >
          <MessageSquare size={16} />
          <span className="hidden lg:inline">Historique</span>
        </button>
      </header>

      {/* --- MAIN LAYOUT (Sidebar + Chat) --- */}
      <div className="flex-1 relative flex overflow-hidden">
        
        {/* SIDEBAR (Drawer sur mobile, Panneau coulissant sur desktop) */}
        <AnimatePresence>
          {isSidebarOpen && (
            <>
              {/* Backdrop Mobile */}
              <motion.div 
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                onClick={() => setIsSidebarOpen(false)}
                className="fixed inset-0 z-30 bg-black/40 backdrop-blur-sm md:hidden"
              />
              
              <motion.div 
                initial={{ x: '-100%', opacity: 0.5 }}
                animate={{ x: 0, opacity: 1 }}
                exit={{ x: '-100%', opacity: 0.5 }}
                transition={{ type: "spring", damping: 25, stiffness: 200 }}
                className="absolute md:relative z-40 w-[280px] h-full bg-white dark:bg-gray-900 border-r border-gray-200 dark:border-gray-800 shadow-2xl md:shadow-none flex flex-col shrink-0"
              >
                <div className="p-4 flex items-center justify-between border-b border-gray-100 dark:border-gray-800/50">
                  <h3 className="font-semibold text-gray-900 dark:text-white">Conversations</h3>
                  <button onClick={() => setIsSidebarOpen(false)} className="p-1 md:hidden text-gray-500 hover:bg-gray-100 rounded-lg">
                    <X size={20} />
                  </button>
                </div>
                
                <div className="p-3 space-y-3">
                  <button 
                    onClick={handleNewSession}
                    className="w-full flex items-center justify-center gap-2 bg-gray-900 hover:bg-black dark:bg-white dark:hover:bg-gray-100 text-white dark:text-gray-900 py-2.5 px-4 rounded-xl transition-all font-medium text-sm shadow-md"
                  >
                    <Plus size={16} /> Nouvelle recherche
                  </button>
                  <div className="relative">
                    <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input 
                      type="text"
                      placeholder="Rechercher..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="w-full bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg pl-9 pr-3 py-2 text-sm text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-pepite-500/30"
                    />
                  </div>
                </div>
                
                <div className="flex-1 overflow-y-auto px-3 pb-4 space-y-1">
                  {filteredSessions.length === 0 ? (
                    <div className="text-center p-6 text-sm text-gray-400">
                      {searchQuery ? "Aucun résultat trouvé." : "Aucune conversation récente."}
                    </div>
                  ) : (
                    filteredSessions.map((session) => (
                      <div 
                        key={session.id}
                        onClick={() => handleLoadSession(session.id)}
                        className={`group relative flex items-center justify-between p-3 rounded-xl cursor-pointer transition-all ${
                          currentSessionId === session.id 
                            ? 'bg-pepite-50 dark:bg-pepite-900/20 border border-pepite-200 dark:border-pepite-800/50' 
                            : 'hover:bg-gray-50 dark:hover:bg-gray-800/50 border border-transparent'
                        }`}
                      >
                        <div className="flex items-center gap-3 overflow-hidden pr-6">
                          <MessageSquare size={14} className={currentSessionId === session.id ? 'text-pepite-600 dark:text-pepite-400' : 'text-gray-400'} />
                          <span className={`truncate text-sm ${currentSessionId === session.id ? 'font-medium text-pepite-700 dark:text-pepite-300' : 'text-gray-600 dark:text-gray-300'}`}>
                            {session.title || 'Discussion'}
                          </span>
                        </div>
                        <button 
                          onClick={(e) => handleDeleteSession(e, session.id)}
                          className="absolute right-2 text-gray-400 opacity-60 group-hover:opacity-100 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/30 p-1.5 rounded-md transition-all"
                          title="Supprimer"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    ))
                  )}
                </div>
              </motion.div>
            </>
          )}
        </AnimatePresence>

        {/* CHAT AREA */}
        <div className="flex-1 flex flex-col min-w-0 relative z-10">
          
          <div className="flex-1 overflow-y-auto p-4 md:p-6 space-y-6 pb-48 md:pb-56 scroll-smooth">
            <div className="max-w-3xl mx-auto space-y-8">
              <AnimatePresence initial={false}>
                {messages.map((msg, idx) => (
                  <motion.div
                    key={idx}
                    initial={{ opacity: 0, y: 15, scale: 0.95 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    transition={{ type: "spring", stiffness: 400, damping: 25 }}
                    className={`flex gap-3 md:gap-4 ${msg.role === 'user' ? 'flex-row-reverse' : ''}`}
                  >
                    {/* Avatar */}
                    <div className={`shrink-0 w-8 h-8 md:w-10 md:h-10 rounded-full flex items-center justify-center shadow-sm ${
                      msg.role === 'model' 
                        ? 'bg-gradient-to-tr from-pepite-600 to-pepite-400 text-white shadow-pepite-500/20' 
                        : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 border border-gray-200 dark:border-gray-700'
                    }`}>
                      {msg.role === 'model' ? <Bot size={18} /> : <User size={18} />}
                    </div>

                    {/* Contenu du message */}
                    <div className={`flex flex-col gap-3 min-w-0 max-w-[85%] md:max-w-[75%] ${msg.role === 'user' ? 'items-end' : 'items-start'}`}>
                      <div className={`px-5 py-3.5 text-[15px] leading-relaxed break-words shadow-sm ${
                        msg.role === 'user'
                          ? 'bg-gray-900 text-white dark:bg-white dark:text-gray-900 rounded-2xl rounded-tr-sm'
                          : 'bg-white dark:bg-gray-900 border border-gray-100 dark:border-gray-800 text-gray-800 dark:text-gray-200 rounded-2xl rounded-tl-sm'
                      }`}>
                        {msg.text}
                      </div>

                      {/* Cartes Produits Recommandés */}
                      {msg.products && msg.products.length > 0 && (
                        <div className="flex gap-4 overflow-x-auto py-2 w-full snap-x pb-4 scrollbar-hide -mx-4 px-4 md:mx-0 md:px-0">
                          {msg.products.map((p, i) => {
                            let imageUrl = 'https://via.placeholder.com/300x400?text=No+Image';
                            try {
                              const parsed = typeof p.images === 'string' ? JSON.parse(p.images) : p.images;
                              if (Array.isArray(parsed) && parsed.length > 0) imageUrl = parsed[0];
                            } catch(e) {}
                            
                            return (
                              <Link 
                                key={i} 
                                to={`/product?id=${p.id}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="shrink-0 w-[160px] md:w-[180px] group flex flex-col gap-2 snap-start outline-none"
                              >
                                <div className="relative aspect-[3/4] rounded-2xl overflow-hidden bg-gray-100 dark:bg-gray-800 shadow-sm border border-gray-200/50 dark:border-gray-700/50">
                                  <img 
                                    src={imageUrl} 
                                    alt={p.name} 
                                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" 
                                    loading="lazy"
                                  />
                                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                                  
                                  {/* Actions rapides: Like & Bookmark */}
                                  <div className="absolute top-2 right-2 flex flex-col gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity duration-300 z-10">
                                    <button 
                                      onClick={(e) => { 
                                        e.preventDefault(); 
                                        e.stopPropagation();
                                        setLikedProducts(prev => {
                                          const newSet = new Set(prev);
                                          newSet.add(p.id);
                                          return newSet;
                                        });
                                        (apiClient as any).entities?.UserInteraction?.create({ product_id: p.id, action: 'like' }).catch(console.error);
                                      }}
                                      className={`p-1.5 backdrop-blur-md rounded-full shadow-lg transition-colors ${likedProducts.has(p.id) ? 'bg-pepite-500 text-white' : 'bg-white/90 dark:bg-gray-900/90 hover:bg-white dark:hover:bg-gray-900 text-gray-400 hover:text-pepite-500'}`}
                                    >
                                      <Heart size={14} className={likedProducts.has(p.id) ? 'fill-current' : ''} />
                                    </button>
                                    <button 
                                      onClick={(e) => { 
                                        e.preventDefault(); 
                                        e.stopPropagation(); 
                                        setCollectionModalProductId(p.id);
                                      }}
                                      className="p-1.5 bg-white/90 dark:bg-gray-900/90 hover:bg-white dark:hover:bg-gray-900 backdrop-blur-md rounded-full shadow-lg text-gray-400 hover:text-amber-500 transition-colors"
                                    >
                                      <Bookmark size={14} />
                                    </button>
                                  </div>
                                  
                                  {/* Bouton d'action superposé */}
                                  <div className="absolute bottom-3 left-1/2 -translate-x-1/2 translate-y-4 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-all duration-300">
                                    <div className="bg-white/90 dark:bg-gray-900/90 backdrop-blur-sm text-gray-900 dark:text-white text-xs font-semibold px-3 py-1.5 rounded-full flex items-center gap-1 shadow-lg">
                                      Voir <ExternalLink size={12} />
                                    </div>
                                  </div>
                                </div>
                                <div className="px-1">
                                  <p className="text-[10px] md:text-xs font-bold text-pepite-600 dark:text-pepite-400 uppercase tracking-widest mb-0.5 truncate">
                                    {p.site_name || p.brand}
                                  </p>
                                  <p className="text-sm font-medium text-gray-900 dark:text-gray-100 line-clamp-1">
                                    {p.name}
                                  </p>
                                  <p className="font-semibold text-gray-700 dark:text-gray-300 text-sm mt-0.5">
                                    {p.price} {p.currency || '€'}
                                  </p>
                                </div>
                              </Link>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </motion.div>
                ))}
                
                {isLoading && (
                  <motion.div
                    initial={{ opacity: 0, y: 15 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="flex gap-3 md:gap-4 max-w-3xl mx-auto"
                  >
                    <div className="shrink-0 w-8 h-8 md:w-10 md:h-10 rounded-full bg-gradient-to-tr from-pepite-600 to-pepite-400 text-white flex items-center justify-center shadow-sm">
                      <Bot size={18} />
                    </div>
                    <div className="bg-white dark:bg-gray-900 border border-gray-100 dark:border-gray-800 rounded-2xl rounded-tl-sm px-5 py-4 flex flex-col gap-3 shadow-sm min-w-[200px]">
                      <div className="flex gap-1.5 items-center text-pepite-600 dark:text-pepite-400">
                        <ThinkingOrb state={thinkingOrbStates[thinkingMessageIndex]} size={20} />
                      </div>
                      <AnimatePresence mode="wait">
                        <motion.span
                          key={thinkingMessageIndex}
                          initial={{ opacity: 0, y: 5 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -5 }}
                          transition={{ duration: 0.2 }}
                          className="text-xs text-gray-500 dark:text-gray-400 font-medium"
                        >
                          {thinkingMessages[thinkingMessageIndex]}
                        </motion.span>
                      </AnimatePresence>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
              <div ref={messagesEndRef} className="h-4" />
            </div>
          </div>

          {/* CHAT INPUT AREA (Glassmorphism, Floating) */}
          <div className="absolute bottom-0 left-0 right-0 p-4 md:p-6 bg-gradient-to-t from-gray-50 via-gray-50/90 to-transparent dark:from-gray-950 dark:via-gray-950/90 pb-safe pointer-events-none">
            <div className="max-w-3xl mx-auto pointer-events-auto">
              
              {/* Icebreakers (Suggestions) */}
              <AnimatePresence>
                {messages.length === 1 && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 10, transition: { duration: 0.2 } }}
                    className="flex flex-wrap gap-2 mb-4 justify-center md:justify-start"
                  >
                    {[
                      "Je cherche un t-shirt oversize avec des motifs floraux",
                      "Trouve-moi un pantalon cargo de style streetwear",
                      "J'ai besoin d'une veste en cuir coupe vintage"
                    ].map((text, idx) => (
                      <button
                        key={idx}
                        type="button"
                        onClick={() => handleSubmit(undefined, text)}
                        className="text-xs md:text-sm bg-white dark:bg-gray-800 hover:bg-pepite-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 hover:text-pepite-600 dark:hover:text-pepite-400 px-4 py-2 rounded-full shadow-sm border border-gray-200/50 dark:border-gray-700/50 transition-colors"
                      >
                        {text}
                      </button>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>

              <form onSubmit={(e) => handleSubmit(e)} className="relative flex items-end bg-white/80 dark:bg-gray-900/80 backdrop-blur-xl border border-gray-200/50 dark:border-gray-700/50 rounded-[28px] shadow-lg shadow-gray-200/20 dark:shadow-black/20 p-1.5 transition-all focus-within:ring-2 focus-within:ring-pepite-500/30 focus-within:bg-white dark:focus-within:bg-gray-900">
                <textarea
                  value={input}
                  onChange={(e) => {
                    setInput(e.target.value);
                    // Auto-resize
                    e.target.style.height = 'auto';
                    e.target.style.height = `${Math.min(e.target.scrollHeight, 120)}px`;
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      handleSubmit(e);
                    }
                  }}
                  disabled={isLoading}
                  placeholder="Je cherche un jorts oversize avec des motifs fleuris..."
                  className="w-full bg-transparent border-none rounded-[24px] pl-5 pr-14 py-4 text-[15px] text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none resize-none max-h-[120px] min-h-[56px]"
                  style={{ height: '56px' }}
                  rows={1}
                />
                <button
                  type="submit"
                  disabled={!input.trim() || isLoading}
                  className="absolute right-3 bottom-3 p-3 bg-pepite-600 hover:bg-pepite-700 disabled:bg-gray-200 dark:disabled:bg-gray-800 disabled:text-gray-400 text-white rounded-full transition-transform active:scale-95 flex items-center justify-center"
                >
                  <Send size={18} className={input.trim() && !isLoading ? 'translate-x-0.5 -translate-y-0.5 transition-transform' : ''} />
                </button>
              </form>
              <div className="text-center mt-3 mb-1 flex flex-col items-center gap-1">
                <span className="text-[10px] text-gray-400 dark:text-gray-500 font-medium">
                  Les recommandations sont générées par l'IA de Pépite.
                </span>
                <span className="text-[10px] text-amber-600/80 dark:text-amber-400/80 font-medium">
                  ⚠️ Ne partagez aucune donnée personnelle ou bancaire avec le Personal Shopper.
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Global styles for hide-scrollbar */}
      <style dangerouslySetInnerHTML={{__html: `
        .scrollbar-hide::-webkit-scrollbar {
            display: none;
        }
        .scrollbar-hide {
            -ms-overflow-style: none;
            scrollbar-width: none;
        }
        @supports (padding-bottom: env(safe-area-inset-bottom)) {
          .pb-safe {
            padding-bottom: calc(1rem + env(safe-area-inset-bottom));
          }
        }
      `}} />
      {/* Modal Limite Atteinte */}
      <AnimatePresence>
        {showRateLimitModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="bg-neutral-900 border border-white/10 p-6 md:p-8 rounded-2xl max-w-md w-full shadow-2xl relative overflow-hidden"
            >
              {/* Effet lumineux */}
              <div className="absolute top-0 left-1/2 -translate-x-1/2 w-3/4 h-32 bg-amber-500/20 blur-[60px] rounded-full pointer-events-none" />

              <div className="relative flex flex-col items-center text-center">
                <div className="w-16 h-16 bg-gradient-to-br from-amber-500/20 to-yellow-500/20 rounded-2xl flex items-center justify-center border border-amber-500/30 mb-6">
                  <Sparkles className="w-8 h-8 text-amber-400" />
                </div>
                
                <h3 className="text-2xl font-bold text-white mb-2">
                  Limite atteinte !
                </h3>
                
                <p className="text-gray-300 mb-6">
                  Vous avez utilisé toutes vos requêtes gratuites en tant qu'invité. Pour continuer à discuter avec l'IA et découvrir plus de Pépites, créez un compte gratuitement !
                </p>

                <div className="flex flex-col w-full gap-3">
                  <a
                    href="/#/register"
                    className="w-full px-6 py-3 bg-gradient-to-r from-amber-500 to-amber-600 text-white font-medium rounded-xl hover:from-amber-600 hover:to-amber-700 transition-all shadow-lg hover:shadow-amber-500/25 flex items-center justify-center"
                  >
                    Créer un compte
                  </a>
                  <button
                    onClick={() => setShowRateLimitModal(false)}
                    className="w-full px-6 py-3 bg-white/5 hover:bg-white/10 text-white font-medium rounded-xl transition-all"
                  >
                    Plus tard
                  </button>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Modal Collection */}
      <AnimatePresence>
        {collectionModalProductId && (
          <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" onClick={() => setCollectionModalProductId(null)}>
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white dark:bg-gray-900 rounded-2xl max-w-md w-full shadow-2xl overflow-hidden relative"
            >
              <button 
                onClick={() => setCollectionModalProductId(null)}
                className="absolute top-4 right-4 p-2 text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full z-10 transition-colors"
              >
                <X size={20} />
              </button>
              <div className="p-6 pt-12 max-h-[80vh] overflow-y-auto">
                <CategoryManager productId={collectionModalProductId} size="default" />
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default PersonalShopperAdmin;
